/**
 * PSET-style washroom focus: horizontal storey cut + top-down ortho camera.
 * Ported from fy27q1-geometry-check/lmv-discover/DiscoverExtension.js
 * (_focusGrayDollhouse, _topView).
 */

/** Clip below floor and above ~2.2 m — removes roof/ceiling, keeps room plan. */
export function applyStoreyCut(
  viewer: Autodesk.Viewing.GuiViewer3D,
  fixtureBox: THREE.Box3
): void {
  const model = viewer.model;
  const unit = model.getUnitScale?.() ?? 1;
  const margin = 0.5 / unit;
  const cutH = 2.2 / unit;
  const floorZ = fixtureBox.min.z - margin;
  const topZ = fixtureBox.min.z + cutH;
  viewer.setCutPlanes([
    new THREE.Vector4(0, 0, -1, floorZ),
    new THREE.Vector4(0, 0, 1, -topZ),
  ]);
}

export function clearStoreyCut(viewer: Autodesk.Viewing.GuiViewer3D): void {
  try {
    viewer.setCutPlanes([]);
  } catch {
    /* viewer may be disposed */
  }
}

/** Gray dollhouse theming — de-emphasises context, highlights target fixture. */
export function applyGrayDollhouse(
  viewer: Autodesk.Viewing.GuiViewer3D,
  targetDbId: number
): void {
  const model = viewer.model;
  const tree = model.getInstanceTree();
  if (!tree) return;

  viewer.setGhosting(false);
  viewer.showAll();
  viewer.clearSelection();
  viewer.clearThemingColors(model);

  const gray = new THREE.Vector4(0.78, 0.78, 0.78, 1);
  const highlight = new THREE.Vector4(1, 0.35, 0, 1);
  const leaves: number[] = [];
  const walk = (id: number) => {
    let hasChild = false;
    tree.enumNodeChildren(id, child => {
      hasChild = true;
      walk(child);
    });
    if (!hasChild) leaves.push(id);
  };
  walk(tree.getRootId());

  for (const id of leaves) {
    if (id !== targetDbId) {
      viewer.setThemingColor(id, gray, model);
    }
  }
  viewer.setThemingColor(targetDbId, highlight, model);
}

export function clearDollhouseTheming(
  viewer: Autodesk.Viewing.GuiViewer3D
): void {
  try {
    viewer.clearThemingColors(viewer.model);
  } catch {
    /* viewer may be disposed */
  }
}

/** Straight-down orthographic plan view centred on measured clear box. */
export function focusTopDownOnBox(
  viewer: Autodesk.Viewing.GuiViewer3D,
  box: THREE.Box3
): void {
  const model = viewer.model;
  const unit = model.getUnitScale?.() ?? 1;
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const span = Math.max(size.x, size.y, 2 / unit) + 1.0 / unit;

  const cam = viewer.getCamera() as THREE.PerspectiveCamera | null;
  const fov = ((cam?.fov ?? 45) * Math.PI) / 180;
  const dist = (span / 2 / Math.tan(fov / 2)) * 2.2;

  const eye = new THREE.Vector3(center.x, center.y, center.z + dist);
  viewer.navigation.setView(eye, center);
  viewer.navigation.setCameraUpVector(new THREE.Vector3(0, 1, 0));

  try {
    viewer.navigation.toOrthographic();
  } catch {
    /* older bundles may lack ortho toggle */
  }

  const fitBox = box.clone();
  fitBox.expandByScalar(0.15);
  viewer.navigation.fitBounds(true, fitBox);
}

export function cutPlaneZ(fixtureBox: THREE.Box3, unitScale: number): number {
  return fixtureBox.min.z + 2.2 / unitScale;
}

/** Oblique ~30° view for property-based violations (windows, doors). */
export function focusObliqueOnBox(
  viewer: Autodesk.Viewing.GuiViewer3D,
  box: THREE.Box3
): void {
  const model = viewer.model;
  const unit = model.getUnitScale?.() ?? 1;
  const c = box.getCenter(new THREE.Vector3());
  const eye = new THREE.Vector3(c.x + 8 / unit, c.y - 8 / unit, c.z + 8 / unit);
  viewer.navigation.setView(eye, c);
  viewer.navigation.setCameraUpVector(new THREE.Vector3(0, 0, 1));
  try {
    viewer.navigation.toPerspective();
  } catch {
    /* optional */
  }
  const fitBox = box.clone();
  fitBox.expandByScalar(0.4);
  viewer.navigation.fitBounds(true, fitBox);
}

/**
 * Pull back to show the whole building while highlighting one element —
 * used for Rooms and other spatially-large context elements whose own
 * 3D bounds are missing or too small to frame usefully.
 */
export function focusBuildingWithElementHighlight(
  viewer: Autodesk.Viewing.GuiViewer3D,
  dbId: number
): void {
  const model = viewer.model;
  if (!model) {
    viewer.select([dbId]);
    viewer.fitToView([dbId]);
    return;
  }

  const modelBox = model.getBoundingBox?.();
  if (!modelBox) {
    viewer.select([dbId]);
    viewer.fitToView([dbId]);
    return;
  }

  clearStoreyCut(viewer);
  applyGrayDollhouse(viewer, dbId);
  viewer.select([dbId]);

  const unit = model.getUnitScale?.() ?? 1;
  const fitBox = modelBox.clone();
  fitBox.expandByScalar(2 / unit);

  const c = fitBox.getCenter(new THREE.Vector3());
  const size = fitBox.getSize(new THREE.Vector3());
  const span = Math.max(size.x, size.y, size.z);

  const eye = new THREE.Vector3(
    c.x + span * 0.55,
    c.y - span * 0.55,
    c.z + span * 0.4
  );
  viewer.navigation.setView(eye, c);
  viewer.navigation.setCameraUpVector(new THREE.Vector3(0, 0, 1));
  try {
    viewer.navigation.toPerspective();
  } catch {
    /* optional */
  }
  viewer.navigation.fitBounds(true, fitBox);
  viewer.impl?.invalidate(true, true, true);
}
